import React, { useEffect, useRef, useMemo, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';
import { MdClose } from 'react-icons/md';
import { getDesiredPositionInsideFrameFromTopSoModalCanBeInsideViewport, IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';

export function ModalHeader({ title, useDefaultCloseButton = true, onClose }) {
    return (
        <div className="modal-control-header">
            <h4 className="modal-control-header__title">{title}</h4>
            {useDefaultCloseButton && (
                <button
                    type="button"
                    className="modal-control-header__close-button"
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    <MdClose className="modal-control-header__close-button-icon" />
                </button>
            )}
        </div>
    );
}

export function ModalBody({ padding, children }) {
    return (
        <div className="modal-control-body" style={{ '--padding': padding }}>
            {children}
        </div>
    );
}

export function ModalFooter({ leftButton, rightButton }) {
    const buttonsArray = [leftButton, rightButton].filter(Boolean);

    const getVariantClassForButton = (variant, button) => {
        if (variant) return `form__button--${variant}`;
        return button === rightButton ? 'form__button--primary' : '';
    };

    return (
        <div className="modal-control-footer">
            {buttonsArray.map((button, index) => (
                <button
                    key={index}
                    type="button"
                    className={`form__button ${getVariantClassForButton(button.variant, button)} ${button === leftButton ? 'modal-control-footer__left-button' : 'modal-control-footer__right-button'}`}
                    onClick={button.onClick}
                    disabled={button.disabled}
                    aria-label={button.ariaLabel}
                >
                    {button.content}
                </button>
            ))}
        </div>
    );
}

function onHeightChange(el, callback, { fireImmediately = true, box = 'border-box' } = {}) {
    let last = -1;

    const measure = () => {
        // Robust read (border-box by default)
        const h = box === 'content-box' ? el.clientHeight : el.getBoundingClientRect().height;
        if (h !== last) {
            last = h;
            callback(h);
        }
    };

    if (fireImmediately) measure();

    let ro; let mo; let interval; let
        onWin;
    if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => measure());
        ro.observe(el);
    } else {
        // Fallback for very old browsers
        mo = new MutationObserver(() => requestAnimationFrame(measure));
        mo.observe(el, { attributes: true, childList: true, subtree: true, characterData: true });
        onWin = () => measure();
        window.addEventListener('resize', onWin);
        interval = setInterval(measure, 400);
    }

    return function stop() {
        if (ro) ro.disconnect();
        if (mo) mo.disconnect();
        if (onWin) window.removeEventListener('resize', onWin);
        if (interval) clearInterval(interval);
    };
}

// INP: overflow se vraća nakon sljedećeg painta da zatvaranje modala ne čeka layout.
function enableBodyScrollAfterNextPaint(className) {
    requestAnimationFrame(() => {
        setTimeout(() => {
            if (document.querySelector(`[data-body-scroll-lock="${className}"]`)) return;
            document.body.classList.remove(className);
            document.body.style.removeProperty('--modal-scrollbar-width');
        }, 0);
    });
}

/*
    leftFooterButton i rightFooterButton su objekti oblika:
    {
        content: HTMLElement,
        onClick: Func,
        variant?: 'primary'|'secondary'|'tertiary', -> odgovara klasama u _forms.sass, default je 'primary' za desni i 'tertiary' za lijevi
        disabled?: Boolean,
        ariaLabel?: string
    }
*/
export default function ModalControl({
    id = 'modalControl',
    className,
    title,
    isOpen,
    onClose,
    disableBodyScroll = true,
    animate = false,
    animateFrom = 'bottom',
    useDefaultCloseButton = true,
    leftFooterButton,
    rightFooterButton,
    backdropColor = '#000',
    backdropOpacity = 0.5,
    desktopWidth = '680px',
    desktopHeight = '680px',
    desktopMaxWidth = '100%',
    desktopMaxHeight = '100%',
    mobileHeight = '90%',
    useBackdrop = true,
    mobileSwipeToClose = true,
    mobileSwipeOnlyOnHeader = false,
    hideOnDesktop = false,
    onEsc,
    children,
}) {
    const modalBackdropRef = useRef(null);
    const modalContainerRef = useRef(null);
    const modalBodyRef = useRef(null);
    const modalHeaderRef = useRef(null);
    const startingTouchPosition = useRef(null);
    const percentageMoved = useRef(null);
    const isTouchMoving = useRef(false);
    const isEligibleForSwipe = useRef(false);
    const initialMovedOffset = useRef(0);
    const areTouchEventsAdded = useRef(false);
    const [wasEverOpen, setWasEverOpen] = useState(isOpen);
    const disableBodyScrollClassName = hideOnDesktop ? 'modalControl--disableBodyScrollOnMobile' : 'modalControl--disableBodyScroll';
    const touchOptions = useRef({ passive: false });

    const handleTouchStart = useRef((e) => {
        if (!e) return;
        const touchedInsideDisabledArea = !!e.target.closest('.disable-modal-swipe-to-close');
        isEligibleForSwipe.current = !touchedInsideDisabledArea && (e.currentTarget === modalHeaderRef.current
                            || (!mobileSwipeOnlyOnHeader && modalBodyRef.current?.scrollTop === 0));
        if (!isEligibleForSwipe.current) return;
        e.stopPropagation();
        startingTouchPosition.current = e.touches[0].clientY;
    });

    const handleTouchMove = useRef((e) => {
        if (!e || !isEligibleForSwipe.current) return;
        isTouchMoving.current = true;
        const sortMobileContentHeight = modalContainerRef.current.clientHeight;
        const movedOffset = e.touches[0].clientY - startingTouchPosition.current;
        if (!initialMovedOffset.current) {
            initialMovedOffset.current = movedOffset;
        }
        if (initialMovedOffset.current <= 0) return;
        e.stopPropagation();
        e.preventDefault();
        const percentageMovedTemp = (movedOffset / sortMobileContentHeight) * 100;
        percentageMoved.current = Math.min(100, Math.max(0, percentageMovedTemp));
        modalContainerRef.current.style.transition = '0s';
        modalContainerRef.current.style.transform = `translateY(${percentageMoved.current}%)`;
        if (modalBackdropRef.current) {
            modalBackdropRef.current.style.transition = '0s';
            modalBackdropRef.current.style.opacity = `${(100 - percentageMoved.current) * backdropOpacity}%`;
        }
    });

    const handleTouchEnd = useRef((e) => {
        if (!isEligibleForSwipe.current) return;
        startingTouchPosition.current = null;
        if (!isTouchMoving.current) return;
        e?.stopPropagation();
        modalContainerRef.current.style.removeProperty('transition');
        modalContainerRef.current.style.removeProperty('transform');
        if (modalBackdropRef.current) {
            modalBackdropRef.current.style.removeProperty('transition');
            modalBackdropRef.current.style.removeProperty('opacity');
        }
        if (percentageMoved.current > 25) onClose();
        isTouchMoving.current = false;
        percentageMoved.current = null;
        initialMovedOffset.current = 0;
    });

    const handleKeyDown = useRef((e) => {
        if (e.key === 'Escape') {
            if (onEsc) onEsc();
        }
    });

    useEffect(() => {
        const handleKeyDownFunc = handleKeyDown.current;
        if (wasEverOpen && onEsc) {
            if (isOpen) {
                document.addEventListener('keydown', handleKeyDownFunc);
            } else {
                document.removeEventListener('keydown', handleKeyDownFunc);
            }
        }
        return () => {
            document?.removeEventListener('keydown', handleKeyDownFunc);
        };
    }, [onEsc, isOpen, wasEverOpen]);

    useEffect(() => {
        modalHeaderRef.current = modalContainerRef.current?.querySelector('.modal-control-header');
        modalBodyRef.current = modalContainerRef.current?.querySelector('.modal-control-body');
        const modalContainerElement = modalContainerRef.current;
        const modalHeaderElement = modalHeaderRef.current;
        const handleTouchStartFunc = handleTouchStart.current;
        const handleTouchMoveFunc = handleTouchMove.current;
        const handleTouchEndFunc = handleTouchEnd.current;
        const touchOptionsObj = touchOptions.current;
        if (!areTouchEventsAdded.current && modalContainerElement && mobileSwipeToClose) {
            modalContainerElement.addEventListener('touchstart', handleTouchStartFunc, touchOptionsObj);
            modalContainerElement.addEventListener('touchmove', handleTouchMoveFunc, touchOptionsObj);
            modalContainerElement.addEventListener('touchend', handleTouchEndFunc, touchOptionsObj);
            if (modalHeaderElement) {
                modalHeaderElement.addEventListener('touchstart', handleTouchStartFunc, touchOptionsObj);
                modalHeaderElement.addEventListener('touchmove', handleTouchMoveFunc, touchOptionsObj);
                modalHeaderElement.addEventListener('touchend', handleTouchEndFunc, touchOptionsObj);
            }
            areTouchEventsAdded.current = true;
        }
        return () => {
            if (modalContainerElement) {
                modalContainerElement.removeEventListener('touchstart', handleTouchStartFunc, touchOptionsObj);
                modalContainerElement.removeEventListener('touchmove', handleTouchMoveFunc, touchOptionsObj);
                modalContainerElement.removeEventListener('touchend', handleTouchEndFunc, touchOptionsObj);
                if (modalHeaderElement) {
                    modalHeaderElement.removeEventListener('touchstart', handleTouchStartFunc, touchOptionsObj);
                    modalHeaderElement.removeEventListener('touchmove', handleTouchMoveFunc, touchOptionsObj);
                    modalHeaderElement.removeEventListener('touchend', handleTouchEndFunc, touchOptionsObj);
                }
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wasEverOpen]);

    useEffect(() => {
        if (!wasEverOpen && isOpen) setWasEverOpen(true);
        if (isOpen) {
            if (IS_FRAME_SERVICE) {
                getDesiredPositionInsideFrameFromTopSoModalCanBeInsideViewport();
            }
        } else {
            // eslint-disable-next-line
             if (modalContainerRef.current) {
                modalContainerRef.current.style.removeProperty('transition');
                modalContainerRef.current.style.removeProperty('transform');
            }
            if (modalBackdropRef.current) {
                modalBackdropRef.current.style.removeProperty('transition');
                modalBackdropRef.current.style.removeProperty('opacity');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useLayoutEffect(() => {
        const el = modalContainerRef.current;
        const stop = el
            ? onHeightChange(el, () => {
                if (IS_FRAME_SERVICE) {
                    getDesiredPositionInsideFrameFromTopSoModalCanBeInsideViewport();
                }
            })
            : () => {};

        return () => stop(); // cleanup on unmount
    }, []);

    useLayoutEffect(() => {
        if (!disableBodyScroll) return undefined;

        if (isOpen) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.setProperty('--modal-scrollbar-width', `${scrollbarWidth}px`);
            document.body.classList.add(disableBodyScrollClassName);
        } else {
            enableBodyScrollAfterNextPaint(disableBodyScrollClassName);
        }

        return () => {
            enableBodyScrollAfterNextPaint(disableBodyScrollClassName);
        };
    }, [isOpen, disableBodyScroll, disableBodyScrollClassName]);

    const getClassName = () => {
        if (!animate) {
            return isOpen ? '' : 'd-none';
        }
        const classNames = [];
        if (wasEverOpen) {
            classNames.push('modal-control--animated', `modal-control--animated-from-${animateFrom}`);
        }
        if (!isOpen) {
            classNames.push('modal-control--animate-out', `modal-control--animate-out-from-${animateFrom}`);
        }
        return classNames.join(' ');
    };

    const getContainerStyle = () => ({
        '--desktop-width': desktopWidth,
        '--desktop-height': desktopHeight,
        '--desktop-max-width': desktopMaxWidth,
        '--desktop-max-height': desktopMaxHeight,
        '--mobile-height': mobileHeight,
        '--desktop-radius': desktopWidth === '100%' && desktopHeight === '100%' ? '0' : '12px',
        '--mobile-radius': mobileHeight === '100%' ? '0' : '12px',
    });

    const childrenArray = useMemo(() => React.Children.toArray(children), [children]);
    const modalHeader = useMemo(() => childrenArray.find((child) => React.isValidElement(child) && child.type === ModalHeader), [childrenArray]);
    const modalBody = useMemo(() => childrenArray.find((child) => React.isValidElement(child) && child.type === ModalBody), [childrenArray]);
    const modalFooter = useMemo(() => childrenArray.find((child) => React.isValidElement(child) && child.type === ModalFooter), [childrenArray]);

    const shouldRenderDefaultHeader = title || useDefaultCloseButton;
    const shouldRenderDefaultFooter = leftFooterButton || rightFooterButton;

    const modalControl = (
        <div
            id={id}
            className={`modal-control ${className ?? ''} ${getClassName()} ${hideOnDesktop ? 'modal-control--hide-on-desktop' : ''}`}
            aria-hidden={!isOpen}
            data-body-scroll-lock={isOpen && disableBodyScroll ? disableBodyScrollClassName : undefined}
        >
            <div
                ref={modalContainerRef}
                className="modal-control-container"
                role="dialog"
                style={getContainerStyle()}
            >
                {modalHeader || modalBody || modalFooter ? (
                    <>
                        {modalHeader ?? (shouldRenderDefaultHeader && (
                            <ModalHeader
                                title={title}
                                useDefaultCloseButton={useDefaultCloseButton}
                                onClose={onClose}
                            />
                        ))}
                        {modalBody}
                        {modalFooter ?? (shouldRenderDefaultFooter && (
                            <ModalFooter
                                leftButton={leftFooterButton}
                                rightButton={rightFooterButton}
                            />
                        ))}
                    </>
                ) : (
                    children
                )}
            </div>
            {useBackdrop && (
                <div
                    ref={modalBackdropRef}
                    role="button"
                    className="modal-control-backdrop"
                    style={{ '--background-color': backdropColor, '--opacity': backdropOpacity }}
                    onClick={onClose}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            onClose();
                        }
                    }}
                    tabIndex={0}
                    aria-label="Close modal"
                />
            )}
        </div>
    );

    return windowManipulationService.isBrowserEnvironment() && wasEverOpen && createPortal(modalControl, document.body);
}
